import React from "react";
import { createFormattersObject } from "../../helpers/utils";
import { useAvailableconjunctionHeaders } from "../../services/configuration";
import { ConjunctionElement } from "./ConjunctionElement";
import { ConjunctionField } from "./ConjunctionField";
import { ConjunctionSummaryCard } from "./ConjunctionSummaryCard";
import type { ConjunctionRiskConfig } from "./conjunctionUiUtils";
import {
  getFormattedDisplayValue,
  getScientificDisplayValue,
} from "./conjunctionUiUtils";

interface ConjunctionInformationDetailsProps {
  conjunction: unknown;
  riskConfig?: ConjunctionRiskConfig;
}

export const ConjunctionInformationDetails: React.FC<
  ConjunctionInformationDetailsProps
> = ({ conjunction, riskConfig }) => {
  const availableConjHeaders = useAvailableconjunctionHeaders();
  const formatters = createFormattersObject(availableConjHeaders.value);

  const relativePositionFields = [
    {
      label: "Miss Distance",
      value: getFormattedDisplayValue(
        conjunction,
        "missDistance.value",
        formatters,
      ),
    },
    {
      label: "Radial Distance",
      value: getFormattedDisplayValue(
        conjunction,
        "radialDistance.value",
        formatters,
      ),
    },
    {
      label: "Along Track Distance",
      value: getFormattedDisplayValue(
        conjunction,
        "alongTrackDistance.value",
        formatters,
      ),
    },
    {
      label: "Cross Track Distance",
      value: getFormattedDisplayValue(
        conjunction,
        "crossTrackDistance.value",
        formatters,
      ),
    },
  ];

  const relativeVelocityFields = [
    {
      label: "Relative Speed",
      value: getScientificDisplayValue(conjunction, "relativeSpeed.value"),
    },
    {
      label: "Radial Velocity",
      value: getScientificDisplayValue(conjunction, "radialVelocity.value"),
    },
    {
      label: "Along Track Velocity",
      value: getScientificDisplayValue(conjunction, "alongTrackVelocity.value"),
    },
    {
      label: "Cross Track Velocity",
      value: getScientificDisplayValue(conjunction, "crossTrackVelocity.value"),
    },
  ];

  const thresholdFields = [
    {
      label: "Miss Distance Threshold",
      value: getFormattedDisplayValue(
        conjunction,
        "missDistanceThreshold.value",
        formatters,
      ),
    },
    {
      label: "Collision Probability Threshold",
      value: getFormattedDisplayValue(
        conjunction,
        "collisionProbabilityThreshold.value",
        formatters,
      ),
    },
    {
      label: "Maximum Collision Probability Threshold",
      value: getFormattedDisplayValue(
        conjunction,
        "maximumCollisionProbabilityThreshold.value",
        formatters,
      ),
    },
  ];

  const probabilityFields = [
    {
      label: "Collision Probability",
      value: getFormattedDisplayValue(
        conjunction,
        "collisionProbability.value",
        formatters,
      ),
    },
    {
      label: "Maximum Collision Probability",
      value: getFormattedDisplayValue(
        conjunction,
        "maximumCollisionProbability.value",
        formatters,
      ),
    },
    {
      label: "Scaled Collision Probability",
      value: getFormattedDisplayValue(
        conjunction,
        "scaledCollisionProbability.value",
        formatters,
      ),
    },
  ];

  return (
    <div className="h-full w-full overflow-y-auto bg-slate-50/40 p-5 !pt-1">
      <div className="flex w-full flex-col gap-5">
        <section className="grid grid-cols-1 items-stretch gap-5 ">
          <ConjunctionSummaryCard
            conjunction={conjunction}
            formatters={formatters}
            riskConfig={riskConfig}
          />

          <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <header className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
              <h2 className="text-base font-semibold text-slate-950">
                Risk & distances
              </h2>
              <p className="mt-1 text-sm font-medium text-slate-500">
                Collision thresholds, separation and relative motion.
              </p>
            </header>
            <div className="min-h-0 flex-1 space-y-4 overflow-auto p-5">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
                  Relative position
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
                  {relativePositionFields.map((field) => (
                    <ConjunctionField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                    />
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
                  Relative velocity
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
                  {relativeVelocityFields.map((field) => (
                    <ConjunctionField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                    />
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
                  Thresholds
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                  {thresholdFields.map((field) => (
                    <ConjunctionField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      tone="muted"
                    />
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
                  Probability
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                  {probabilityFields.map((field) => (
                    <ConjunctionField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      tone="muted"
                    />
                  ))}
                </div>
              </section>
            </div>
          </section>
        </section>

        <section className="grid grid-cols-1 items-stretch gap-5 xl:grid-cols-2">
          <ConjunctionElement
            conjunction={conjunction}
            orbitElement="target"
            formatters={formatters}
          />
          <ConjunctionElement
            conjunction={conjunction}
            orbitElement="chaser"
            formatters={formatters}
          />
        </section>
      </div>
    </div>
  );
};
